import React from 'react';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from './Pagination';

interface TruncatedPaginationProps {
  currentPage: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}

function getPagesToRender(currentPage: number, pageCount: number): Array<number | 'ellipsis'> {
  const pages: Array<number | 'ellipsis'> = [];

  // If there are 7 or fewer pages, display all.
  if (pageCount <= 7) {
    for (let i = 1; i <= pageCount; i++) {
      pages.push(i);
    }
    return pages;
  }

  // Always show the first page.
  pages.push(1);

  // Show ellipsis if current page is greater than 3.
  if (currentPage > 3) {
    pages.push('ellipsis');
  }

  // Determine range for pages around currentPage:
  const start = Math.max(2, currentPage - 2);
  const end = Math.min(pageCount - 1, currentPage + 2);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  // Show ellipsis if current page is less than pageCount - 2.
  if (currentPage < pageCount - 3) {
    pages.push('ellipsis');
  }

  // Always show the last page.
  pages.push(pageCount);

  return pages;
}

export const SimplePagination: React.FC<TruncatedPaginationProps> = ({ currentPage, pageCount, onPageChange }) => {
  const pagesToRender = getPagesToRender(currentPage, pageCount);
  let ellipsisCount = 0;

  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} />
        </PaginationItem>

        {pagesToRender.map(pageOrEllipsis => {
          if (pageOrEllipsis === 'ellipsis') {
            ellipsisCount++;
            const key = ellipsisCount === 1 ? 'ellipsis-start' : 'ellipsis-end';
            return (
              <PaginationItem key={key}>
                <PaginationEllipsis />
              </PaginationItem>
            );
          }
          return (
            <PaginationItem key={`page-${pageOrEllipsis}`}>
              <PaginationLink isActive={pageOrEllipsis === currentPage} onClick={() => onPageChange(pageOrEllipsis)}>
                {pageOrEllipsis}
              </PaginationLink>
            </PaginationItem>
          );
        })}

        <PaginationItem>
          <PaginationNext onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === pageCount} />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};
